import React from 'react';
import { Search, Filter, Calendar, Clock, MapPin, Download } from 'lucide-react';
import { MovementType } from '../../../types/movement';
import { MovementTypeBadge } from '../badges/MovementTypeBadge';
import { exportFilteredMovements } from '../../../utils/exportMovements';
import toast from 'react-hot-toast';
import { usePilgrimsStore } from '../../../stores/pilgrimsStore';
import { usePackagesStore } from '../../../stores/packagesStore';
import { useFlightsStore } from '../../../stores/flightsStore';
import { useLanguage } from '../../../contexts/LanguageContext';

type FilterState = {
  search: string;
  type: string;
  fromCity: string;
  toCity: string;
  dateRange: {
    start: string;
    end: string;
  };
  timeRange: {
    start: string;
    end: string;
  };
  minPilgrims: string;
  maxPilgrims: string;
};

type Props = {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: any) => void;
  onClearFilters: () => void;
  filteredMovements: Movement[];
};

export const MovementFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  filteredMovements = []
}: Props) => {
  const { translations } = useLanguage();
  const { pilgrims } = usePilgrimsStore();
  const { packages } = usePackagesStore();
  const { flights } = useFlightsStore();

  const hasActiveFilters = () => {
    return filters.search || 
           filters.type !== 'all' || 
           filters.fromCity !== 'all' ||
           filters.toCity !== 'all' ||
           filters.dateRange.start ||
           filters.dateRange.end ||
           filters.timeRange.start ||
           filters.timeRange.end ||
           filters.minPilgrims ||
           filters.maxPilgrims;
  };

  const handleExport = () => {
    try {
      exportFilteredMovements(
        filteredMovements,
        pilgrims,
        packages,
        flights,
        translations
      );
      toast.success(translations?.movements?.exportSuccess || 'Movements exported successfully');
    } catch (error) {
      toast.error(translations?.movements?.exportError || 'Failed to export movements');
    }
  };

  const getCityName = (city: string) => {
    switch(city) {
      case 'makkah':
        return translations?.cities?.makkah || 'Makkah';
      case 'madinah':
        return translations?.cities?.madinah || 'Madinah';
      case 'jeddah':
        return translations?.cities?.jeddah || 'Jeddah';
      default:
        return city;
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
      {/* Main Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={translations?.movements?.searchPlaceholder || 'Search by movement name or location...'}
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-right
                     focus:ring-2 focus:ring-primary-500 transition-all"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Movement Type */}
        <select
          value={filters.type}
          onChange={(e) => onFilterChange('type', e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">{translations?.movements?.allMovements || 'All Movements'}</option>
          <option value="arrival">{translations?.movements?.arrival || 'Pilgrims Arrival'}</option>
          <option value="hotel_transfer">{translations?.movements?.hotelTransfer || 'Hotel Transfer'}</option>
          <option value="camp_departure">{translations?.movements?.campDeparture || 'Camp Departure'}</option>
          <option value="camp_return">{translations?.movements?.campReturn || 'Camp Return'}</option>
          <option value="departure">{translations?.movements?.departure || 'Pilgrims Departure'}</option>
        </select>

        {/* From City */}
        <select
          value={filters.fromCity}
          onChange={(e) => onFilterChange('fromCity', e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">{translations?.movements?.allCitiesFrom || 'All Cities (From)'}</option>
          <option value="makkah">{translations?.cities?.makkah || 'Makkah'}</option>
          <option value="madinah">{translations?.cities?.madinah || 'Madinah'}</option>
          <option value="jeddah">{translations?.cities?.jeddah || 'Jeddah'}</option>
        </select>

        {/* To City */}
        <select
          value={filters.toCity}
          onChange={(e) => onFilterChange('toCity', e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">{translations?.movements?.allCitiesTo || 'All Cities (To)'}</option>
          <option value="makkah">{translations?.cities?.makkah || 'Makkah'}</option>
          <option value="madinah">{translations?.cities?.madinah || 'Madinah'}</option>
          <option value="jeddah">{translations?.cities?.jeddah || 'Jeddah'}</option>
        </select>
      </div>

      {/* Advanced Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
        {/* Date Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {translations?.movements?.dateRange || 'Movement Date'}
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.dateRange.start}
              onChange={(e) => onFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="date"
              value={filters.dateRange.end}
              onChange={(e) => onFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Time Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {translations?.movements?.timeRange || 'Movement Time'}
          </label>
          <div className="flex gap-2">
            <input
              type="time"
              value={filters.timeRange.start}
              onChange={(e) => onFilterChange('timeRange', { ...filters.timeRange, start: e.target.value })}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="time"
              value={filters.timeRange.end}
              onChange={(e) => onFilterChange('timeRange', { ...filters.timeRange, end: e.target.value })}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Pilgrims Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {translations?.movements?.pilgrimsCount || 'Pilgrims Count'}
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder={translations?.movements?.from || 'From'}
              value={filters.minPilgrims}
              onChange={(e) => onFilterChange('minPilgrims', e.target.value)}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
              min="0"
            />
            <input
              type="number"
              placeholder={translations?.movements?.to || 'To'}
              value={filters.maxPilgrims}
              onChange={(e) => onFilterChange('maxPilgrims', e.target.value)}
              className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500"
              min="0"
            />
          </div>
        </div>

        {/* Export Button */}
        <div className="flex items-end">
          <button
            onClick={handleExport}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 
                     bg-primary-50 text-primary-600 rounded-lg 
                     hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span>
              {translations?.movements?.export || 'Export'} ({filteredMovements.length})
            </span>
          </button>
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters() && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">
              {translations?.movements?.activeFilters || 'Active Filters'}:
            </span>
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.movements?.search || 'Search'}: {filters.search}
                </span>
              )}
              {filters.type !== 'all' && (
                <MovementTypeBadge type={filters.type as MovementType} />
              )}
              {filters.fromCity !== 'all' && (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  {translations?.movements?.from || 'From'}: {getCityName(filters.fromCity)}
                </span>
              )}
              {filters.toCity !== 'all' && (
                <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                  {translations?.movements?.to || 'To'}: {getCityName(filters.toCity)}
                </span>
              )}
              {(filters.dateRange.start || filters.dateRange.end) && (
                <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded">
                  {translations?.movements?.date || 'Date'}: {filters.dateRange.start} - {filters.dateRange.end}
                </span>
              )}
              {(filters.minPilgrims || filters.maxPilgrims) && (
                <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                  {translations?.movements?.pilgrimsCount || 'Pilgrims'}: {filters.minPilgrims || '0'} - {filters.maxPilgrims || '∞'}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClearFilters}
            className="text-sm text-red-500 hover:text-red-600"
          >
            {translations?.movements?.clearFilters || 'Clear Filters'}
          </button>
        </div>
      )}
    </div>
  );
};